import { PropsWithChildren, useRef } from 'react';

import { Box, Paper } from '@mui/material';

import useInfiniteScroll from '@/hooks/useInfiniteScroll';

import NotificationFeedHeader from './NotificationFeedHeader';

type NotificationFeedWrapperProps = {
  onFetchMore?: () => void;
  canFetchMore?: boolean;
};

const NotificationFeedWrapper = ({
  onFetchMore,
  canFetchMore,
  children,
}: PropsWithChildren<NotificationFeedWrapperProps>) => {
  const feedRef = useRef<HTMLDivElement | null>(null);

  const isFeedScrollable =
    feedRef.current &&
    feedRef.current.scrollHeight > feedRef.current.clientHeight;

  useInfiniteScroll({
    element: feedRef,
    onLoad: () => onFetchMore?.(),
    options: {
      distance: 50,
      interval: 500,
      canLoad: ({ scrollHeight, clientHeight }) => {
        const isScrollable = scrollHeight > clientHeight;

        return isScrollable && !!canFetchMore;
      },
    },
  });

  const classes = [
    'notification-feed',
    isFeedScrollable ? 'scrollable' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Paper
      className={classes}
      sx={{
        width: 440,
        p: 0,
        border: 0,
        bgcolor: 'neutral50',
        '& .rnf-notification-feed': {
          borderRadius: '6px',
        },
        '& .feed-cell + .feed-cell': {
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '&:not(.scrollable) .feed-cell:last-child': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
      data-cy="notification-feed"
    >
      <NotificationFeedHeader />

      <Box
        ref={feedRef}
        sx={{
          width: '100%',
          height: '680px',
          overflowY: 'auto',
        }}
      >
        {children}
      </Box>
    </Paper>
  );
};

export default NotificationFeedWrapper;
